import { useTranslation } from 'react-i18next'
import { ChevronLeft } from 'lucide-react'
import { PublicNavigationBar } from '../components/PublicNavigationBar'

// Import the cookie policy content
const enCookiePolicyContent = `# Cookie Policy

## 1. What are the cookies?

Cookies are small text files stored on your device (computer, tablet, or mobile phone) when you sign into websites, including www.ranch.gr and www.theranch.gr. Cookies allow us to remember your preferences and choices when you enter our site (language selection and form filling, for a limited time).

## 2. What kind of cookies do we use?

### Necessary cookies
**Category:** Necessary cookies  
**Purpose:** These cookies are necessary for the operation of the website. They are usually installed after you have entered your preferences and settings (e.g. filling in forms, language selection).  
**Storage Time:** 2 hours  
**Consequences of Disablement:** If you disable these cookies, we will not have the ability to know the language that you have chosen to view the website in. Furthermore the completion and submission of various electronic forms will not be possible.

### Performance cookies
**Category:** Performance cookies (GoogleAnalytics)  
**Purpose:** These cookies allow us to understand how our users interact with our website and, which pages were visited, as well as the original entrance point. In this way, we improve our performance to provide the best online experience.  
**Storage Time:** 24 hours – 2 years  
**Consequences of Disablement:** If you disable these cookies, we will not have the ability to monitor your movement on our website, and as a result your experience in our website will be of inferior quality.

## 3. How can I disable cookies?

You can easily customize your browser settings to enable or disable cookies. To disable cookies please refer to the feature "Help" in your browser settings. For more information about how cookies work, you can visit the following link: http://www.aboutcookies.org.`

const elCookiePolicyContent = `# Πολιτική Cookies

## 1. Τι είναι τα cookies;
Τα cookies είναι μικρά αρχεία κειμένου που αποθηκεύονται στη συσκευή σας (υπολογιστή, tablet ή κινητό τηλέφωνο) όταν συνδέεστε στους ιστότοπους www.ranch.gr και www.theranch.gr. Τα cookies μας παρέχουν τη δυνατότητα να θυμόμαστε τις προτιμήσεις και τις επιλογές σας κατά την είσοδό σας στην ιστοσελίδα μας (επιλογή γλώσσας, συμπλήρωση φόρμας, για ένα περιορισμένο χρονικό διάστημα.

## 2. Τι είδους cookies χρησιμοποιούμε;

### Necessary cookies
**Κατηγορία:** Necessary cookies  
**Σκοπός:** Τα cookies αυτά είναι απαραίτητα για τη λειτουργία της ιστοσελίδας. Συνήθως εγκαθίστανται έπειτα από εισαγωγή δικών σας ρυθμίσεων και προτιμήσεων (π.χ. συμπλήρωση φόρμας, επιλογή γλώσσας).  
**Χρόνος αποθήκευσης:** 2 ώρες  
**Συνέπειες απενεργοποίησης:** Εάν απενεργοποιήσετε αυτά τα cookies δεν θα έχουμε τη δυνατότητα να γνωρίζουμε την γλώσσα προβολής της ιστοσελίδας που έχετε επιλέξει, όπως επίσης δεν θα μπορεί να ολοκληρωθεί η συμπλήρωση και υποβολή διαφόρων ηλεκτρονικών φορμών.

### Performance cookies
**Κατηγορία:** Performance cookies (GoogleAnalytics)  
**Σκοπός:** Τα cookies αυτά μας επιτρέπουν να κατανοήσουμε πώς αλληλεπιδρούν οι χρήστες με την ιστοσελίδα μας και συγκεκριμένα, ποια σημεία επισκέφθηκαν καθώς και την προέλευση της κίνησης. Με τον τρόπο αυτό βελτιώνουμε τις επιδόσεις και σας παρέχουμε μια καλύτερη διαδικτυακή εμπειρία.  
**Χρόνος αποθήκευσης:** 24 ώρες – 2 χρόνια  
**Συνέπειες απενεργοποίησης:** Εάν απενεργοποιήσετε αυτά τα cookies δεν θα έχουμε τη δυνατότητα να παρακολουθούμε την κίνησή σας στην ιστοσελίδα μας και θα σας παρέχουμε μια διαδικτυακή εμπειρία χαμηλότερης ποιότητας.

## 3. Πώς μπορώ να απενεργοποιήσω τα cookies;
Μπορείτε εύκολα να προσαρμόσετε τις ρυθμίσεις του προγράμματος περιήγησής σας που αφορούν την ενεργοποίηση ή απενεργοποίηση των cookies. Προκειμένου να απενεργοποιήσετε τα cookies παρακαλούμε ανατρέξτε στη λειτουργία «Βοήθεια» του προγράμματος περιήγησής σας. Για περισσότερες πληροφορίες σχετικά με τη λειτουργία των cookiesμπορείτε να επισκεφθείτε τον παρακάτω σύνδεσμο: http://www.aboutcookies.org.`

export function CookiePolicyPage() {
  const { t, i18n } = useTranslation(['common'])
  const currentLanguage = i18n.language

  // Choose content based on current language
  const cookiePolicyContent = currentLanguage === 'el' ? elCookiePolicyContent : enCookiePolicyContent

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicNavigationBar />
      <main className="flex-grow max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8 w-full">
        <a href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
          <ChevronLeft className="h-4 w-4" />
          {t('backToHome')}
        </a>
        <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 sm:p-8">
          <h1 className="text-xl font-semibold text-foreground mb-6">{t('legal.cookiePolicy')}</h1>
          <div className="max-w-none text-sm leading-relaxed text-muted-foreground">
            <div dangerouslySetInnerHTML={{ 
              __html: cookiePolicyContent.split('\n').map(line => {
                if (line.startsWith('# ')) return `<h1 class="text-lg font-semibold text-foreground mt-8 mb-3">${line.replace('# ', '')}</h1>`;
                if (line.startsWith('## ')) return `<h2 class="text-base font-semibold text-foreground mt-6 mb-2">${line.replace('## ', '')}</h2>`;
                if (line.startsWith('### ')) return `<h3 class="text-sm font-semibold text-foreground mt-4 mb-1.5">${line.replace('### ', '')}</h3>`;
                if (line.startsWith('- ')) return `<li class="ml-5 mb-1.5 list-disc">${line.replace('- ', '')}</li>`;
                if (line.trim() === '') return '<div class="h-2"></div>';
                if (line.includes('**')) return `<p class="mb-3">${line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-medium text-foreground">$1</strong>')}</p>`;
                return `<p class="mb-3">${line}</p>`;
              }).join('')
            }} />
          </div>
        </div>
      </main>
    </div>
  )
} 